/**
 * 見積依頼の車両入力フォーム（DMD_Requests 系フィールド）と
 * GET /car/search クエリ・CarSearch 行の橋渡し。
 * @see ./README.md
 */

/**
 * 車輌検索ダイアログの結果一覧列（車輌指定画面と同じ名称を基本にする）
 * 型式指定は carModelNumber、車輌型式は carSharyoKatashiki（行データ）。
 */
export const CAR_SEARCH_LIST_COLUMNS = [
  { key: "carMakerName", label: "メーカー" },
  { key: "carName", label: "車名" },
  { key: "carSharyoKatashiki", label: "車輌型式" },
  { key: "carNumber", label: "車台番号" },
  { key: "carModelNumber", label: "型式指定" },
  { key: "carTypeNumber", label: "類別区分" },
  { key: "carTrimNo", label: "トリム番号" },
  { key: "carModelYear", label: "年式" },
  { key: "carGrade", label: "グレード" },
  { key: "carColorName", label: "カラー名称" },
  { key: "carEngineModel", label: "エンジン型式" },
  { key: "carTransmission", label: "シフト方式" },
  { key: "carRemarkNm", label: "装備情報" },
  { key: "carBodyNm", label: "ボディタイプ" },
  { key: "carSyaType", label: "車型グループ" },
];

const s = (v) => (v == null ? "" : String(v)).trim();

const pad5 = (v) => s(v).padStart(5, "0").slice(0, 5);
const pad4 = (v) => s(v).padStart(4, "0").slice(0, 4);

/**
 * p_car 1件（PcarApiCarRow）から親フォーム用パッチを生成
 * @param {import("./pCarApiTypes.js").PcarApiCarRow} api
 * @returns {Record<string, string>}
 */
export function patchFromPcarApiCarRow(api) {
  const patch = {};
  if (api.KATA_SITEI != null && String(api.KATA_SITEI).trim() !== "") {
    patch.Katashiki = pad5(api.KATA_SITEI);
  }
  if (api.RUIBETU != null && String(api.RUIBETU).trim() !== "") {
    patch.Ruibetsu = pad4(api.RUIBETU);
  }
  if (s(api.SYASYU_NM)) patch.CarName = s(api.SYASYU_NM);
  if (s(api.FULL_KATA_ER)) patch.Sharyo_Katashiki = s(api.FULL_KATA_ER);
  else if (s(api.FULL_KATA)) patch.Sharyo_Katashiki = s(api.FULL_KATA);
  if (s(api.MODEL_PLATE)) patch.Shadaibango = s(api.MODEL_PLATE);
  if (api.C_MAKER_CD != null && String(api.C_MAKER_CD).trim() !== "") patch.MakerCD = s(api.C_MAKER_CD);
  const nenYm = api.SNENST ?? api.SNEN ?? api.SNENED;
  if (nenYm != null && String(nenYm).length >= 4) {
    patch.Sharyo_Nenshiki = String(nenYm).slice(0, 4);
  }
  if (s(api.CARGRADE_NM)) patch.Sharyo_Grade = s(api.CARGRADE_NM);
  if (s(api.COLOR_NM)) patch.Sharyo_Color = s(api.COLOR_NM);
  if (s(api.TRIM_NM)) patch.Sharyo_TrimNo = s(api.TRIM_NM);
  const engineRow = {
    carEngineModel: s(api.ENGINE_NM),
    carMakerName: s(api.MAKER_NM),
    carForcedInduction: s(api.INTAKE_AND_EXHAUST_NM),
  };
  const engine = buildCarSearchEngineKatashikiPatchValue(engineRow);
  if (s(api.ENGINE_NM)) patch.Sharyo_EngineKatashiki = engine || s(api.ENGINE_NM);
  if (s(api.FUEL)) patch.Sharyo_Ekubun = s(api.FUEL);
  const tm = s(api.MISSION_NM) || s(api.GEARBOX_NM);
  if (tm) patch.Sharyo_TransMission = tm;
  if (s(api.REMARK_NM)) patch.Sharyo_Remark_Nm = s(api.REMARK_NM);
  if (s(api.BODY_NM)) patch.Sharyo_Body_Nm = s(api.BODY_NM);
  if (s(api.SYA_TYPE)) patch.Sharyo_Sya_Type = s(api.SYA_TYPE);
  return patch;
}

/**
 * スバル車はエンジン型式末尾の T 付与対象外（仕様）
 * API 側で `carMakerSubaru: true` を返してもよい
 * @param {Record<string, unknown>} row
 */
function isSubaruMakerRow(row) {
  if (row.carMakerSubaru === true) return true;
  const name = s(row.carMakerName);
  if (/スバル/i.test(name) || /^subaru$/i.test(name)) return true;
  return false;
}

/**
 * 過給機表記からターボ等を推定（専用列が無い場合はエンジン型式末尾 T の代替）
 * @param {unknown} v
 */
function forcedInductionImpliesTurboSuffix(v) {
  const t = s(v);
  if (!t) return false;
  return /ターボ|ﾀｰﾎﾞ|turbo|スーパーチャ|ｽｰﾊﾟｰﾁｬ|super\s*charg|過給|ブースト|ツインスク|twinturbo|biturbo/i.test(t);
}

/**
 * 親フォームの Sharyo_EngineKatashiki へ入れる値（過給機→末尾 T、スバル除く）
 * @param {Record<string, unknown>} row
 */
export function buildCarSearchEngineKatashikiPatchValue(row = {}) {
  const base = s(row.carEngineModel);
  if (!base) return "";
  if (row.carSkipEngineTurboT === true) return base;
  if (!forcedInductionImpliesTurboSuffix(row.carForcedInduction)) return base;
  if (isSubaruMakerRow(row)) return base;
  if (/[tT]$/.test(base)) return base;
  return `${base}T`;
}

/**
 * 親フォーム → 車輌検索ダイアログの初期条件（p_car で実用上効く項目のみ）
 * @param {Record<string, string | number | undefined | null>} form
 * @returns {{
 *   carModelNumber: string;
 *   carTypeNumber: string;
 *   carSharyoKatashiki: string;
 *   carNumber: string;
 *   carMakerCode: string;
 * }}
 */
export function formToCarSearchCriteria(form = {}) {
  return {
    carModelNumber: s(form.Katashiki),
    carTypeNumber: s(form.Ruibetsu),
    carSharyoKatashiki: s(form.Sharyo_Katashiki),
    carNumber: s(form.Shadaibango),
    carMakerCode: s(form.MakerCD),
  };
}

/**
 * 検索結果1行（CarSearch 相当）から車両フォームへマージする部分オブジェクトを返す。
 * `row.pcar` があるときは p_car 応答（PcarApiCarRow）を正としてマッピングする。
 * 一覧に出していない項目（フルモデル等）は行にあってもここでは反映しない。
 * 型式指定は Katashiki。車輌型式は `carSharyoKatashiki`、旧 API 互換で `carModel` も Sharyo_Katashiki に流用可。
 * @param {Record<string, string | undefined | null>} row
 * @returns {Record<string, string>}
 */
export function carSearchRowToSyaryoFormPatch(row = {}) {
  /** @type {Record<string, string>} */
  let patch;
  if (row.pcar && typeof row.pcar === "object") {
    patch = patchFromPcarApiCarRow(/** @type {import("./pCarApiTypes.js").PcarApiCarRow} */ (row.pcar));
  } else {
    patch = {};
    if (row.carModelNumber != null && row.carModelNumber !== "") patch.Katashiki = s(row.carModelNumber);
    if (row.carTypeNumber != null && row.carTypeNumber !== "") patch.Ruibetsu = s(row.carTypeNumber);
    if (row.carName != null && row.carName !== "") patch.CarName = s(row.carName);
    if (row.carNumber != null && row.carNumber !== "") patch.Shadaibango = s(row.carNumber);
    const nenshiki = s(row.carModelYear) || s(row.carProductionYm);
    if (nenshiki) patch.Sharyo_Nenshiki = nenshiki;
    if (row.carMakerCode != null && row.carMakerCode !== "") patch.MakerCD = s(row.carMakerCode);
    const sharyoKatashiki = s(row.carSharyoKatashiki) || s(row.carModel);
    if (sharyoKatashiki) patch.Sharyo_Katashiki = sharyoKatashiki;
    if (row.carColorNo != null && row.carColorNo !== "") patch.Sharyo_ColorNo = s(row.carColorNo);
    if (row.carTrimNo != null && row.carTrimNo !== "") patch.Sharyo_TrimNo = s(row.carTrimNo);
    if (row.carColorName != null && row.carColorName !== "") patch.Sharyo_Color = s(row.carColorName);
    if (row.carGrade != null && row.carGrade !== "") patch.Sharyo_Grade = s(row.carGrade);
    const engine = buildCarSearchEngineKatashikiPatchValue(row);
    if (engine) patch.Sharyo_EngineKatashiki = engine;
    if (row.carFuelType != null && row.carFuelType !== "") patch.Sharyo_Ekubun = s(row.carFuelType);
    if (row.carTransmission != null && row.carTransmission !== "") patch.Sharyo_TransMission = s(row.carTransmission);
    if (row.carRemarkNm != null && row.carRemarkNm !== "") patch.Sharyo_Remark_Nm = s(row.carRemarkNm);
    if (row.carBodyNm != null && row.carBodyNm !== "") patch.Sharyo_Body_Nm = s(row.carBodyNm);
    if (row.carSyaType != null && row.carSyaType !== "") patch.Sharyo_Sya_Type = s(row.carSyaType);
  }
  // p_car は MODEL_PLATE を返さないことが多い。一覧の carNumber（検索条件補完）を車台番号へ。
  if (!s(patch.Shadaibango) && s(row.carNumber)) {
    patch.Shadaibango = s(row.carNumber);
  }
  return patch;
}
